import React, { useState } from 'react';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import Sheet from '@mui/joy/Sheet';
import Stack from '@mui/joy/Stack';
import Grid from '@mui/joy/Grid';
import Chip from '@mui/joy/Chip';
import IconButton from '@mui/joy/IconButton';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Address } from '../../types/address';
import AddressForm from './AddressForm';

interface AddressListProps {
  addresses: Address[];
  clientReference: string;
  onAddressCreated: (address: Address) => void;
  onAddressUpdated: (address: Address) => void;
  onAddressDeleted: (reference: string) => void;
}

const AddressList: React.FC<AddressListProps> = ({
  addresses,
  clientReference,
  onAddressCreated,
  onAddressUpdated,
  onAddressDeleted,
}) => {
  const [showForm, setShowForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState<Address | null>(null);
  const list = Array.isArray(addresses) ? addresses : [];

  return (
    <Box sx={{ mt: 1.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography level="title-lg">Direcciones</Typography>
        <Button
          size="sm"
          color="primary"
          onClick={() => {
            setEditingAddress(null);
            setShowForm(true);
          }}
        >
          Agregar dirección
        </Button>
      </Stack>

      {showForm && (
        <AddressForm
          clientReference={clientReference}
          address={editingAddress}
          onSaved={(addr) => {
            if (editingAddress) {
              onAddressUpdated(addr);
            } else {
              onAddressCreated(addr);
            }
            setShowForm(false);
            setEditingAddress(null);
          }}
          onCancel={() => {
            setShowForm(false);
            setEditingAddress(null);
          }}
        />
      )}

      <Grid container spacing={2}>
        {list.map((address) => (
          <Grid key={address.reference} xs={12} md={6}>
            <Sheet
              variant="outlined"
              color={address.isDefault ? 'success' : 'neutral'}
              sx={{ p: 2 }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  {address.isDefault ? (
                    <Chip size="sm" color="success" variant="soft" sx={{ mb: 1 }}>
                      Principal
                    </Chip>
                  ) : null}
                  <Typography level="title-sm">
                    {address.street} {address.number}
                  </Typography>
                  <Typography level="body-sm" sx={{ color: 'neutral.600' }}>
                    {address.city}, {address.state}
                  </Typography>
                  <Typography level="body-sm" sx={{ color: 'neutral.600' }}>
                    CP: {address.zipCode}
                  </Typography>
                  {address.reference_note ? (
                    <Typography level="body-xs" sx={{ color: 'neutral.500', mt: 1 }}>
                      Descripción: {address.reference_note}
                    </Typography>
                  ) : null}
                </Box>
                <Stack direction="row" spacing={0.5}>
                  <IconButton
                    size="sm"
                    variant="plain"
                    color="primary"
                    onClick={() => {
                      setEditingAddress(address);
                      setShowForm(true);
                    }}
                  >
                    <EditOutlinedIcon />
                  </IconButton>
                  <IconButton
                    size="sm"
                    variant="plain"
                    color="danger"
                    onClick={() => onAddressDeleted(address.reference)}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Stack>
              </Stack>
            </Sheet>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default AddressList;
